import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HiOutlineMagnifyingGlass,
  HiOutlinePlus,
  HiChevronLeft,
  HiChevronRight,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineChevronDown,
} from "react-icons/hi2";
import { IoRestaurant } from "react-icons/io5";
import RecipeCard from "../../components/cards/RecipeCard.jsx";
import { useLanguage } from "../../contexts/LanguageContext";
import { useRecipesTranslation } from "../../components/common/RecipeTranslator";
import { useRecipePopup } from "../../contexts/RecipePopupContext";
import API from "../../config/api.js";
import { foodBg } from "../../assets/images";
import { CardGridLoading } from "../../components/common/CardGridLoading";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
};

/**
 * Page des recettes : quand l'utilisateur est connecté, affiche uniquement ses recettes (mine=1).
 * Sans connexion, affiche toutes les recettes publiques.
 */
const LIMIT = 10;

const RecipesPage = ({ user, token }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const tt = (key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  };
  const { openEditPopup } = useRecipePopup();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDifficulty, setFilterDifficulty] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const isAdminUser =
    String(user?.role || "").toLowerCase() === "admin" ||
    Boolean(user?.is_admin || user?.isAdmin);

  const loadRecipes = useCallback(
    async (pageNum = 1) => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({
          limit: String(LIMIT),
          page: String(pageNum),
          ...(searchTerm && { search: searchTerm }),
          ...(filterDifficulty !== "all" && { difficulty: filterDifficulty }),
          ...(token && !isAdminUser && { mine: "1" }),
        });
        const response = await fetch(`${API.baseURL}/recipes?${params}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (response.ok) {
          const data = await response.json();
          setRecipes(data.data || data || []);
          setTotalCount(data.total ?? (data.data || data || []).length);
          setTotalPages(data.totalPages ?? 1);
          setPage(pageNum);
        } else {
          setError(t("errorLoading"));
        }
      } catch (err) {
        console.error("Erreur lors du chargement des recettes:", err);
        setError(t("connectionError"));
      } finally {
        setLoading(false);
      }
    },
    [token, searchTerm, filterDifficulty, t, isAdminUser],
  );

  /** Charger les recettes au montage, à la connexion/déconnexion, et quand recherche ou filtre changent (toujours page 1) */
  useEffect(() => {
    loadRecipes(1);
  }, [searchTerm, filterDifficulty, token]);

  /** Changement de page (boutons pagination) */
  const goToPage = (newPage) => {
    const p = Math.max(1, Math.min(totalPages, newPage));
    if (p !== page) loadRecipes(p);
  };

  /**
   * Traduction automatique des recettes
   */
  const { translatedRecipes, isTranslating } = useRecipesTranslation(recipes);

  /**
   * Gérer l'ajout/suppression de favori
   */
  const handleFavoriteToggle = async (recipeId, isFavorite) => {
    if (!user) {
      alert(t("mustBeLoggedIn"));
      return;
    }

    try {
      const url = isFavorite
        ? `http://localhost:3000/api/favorites/remove/${recipeId}`
        : `http://localhost:3000/api/favorites/add/${recipeId}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        console.log(
          `Recette ${recipeId} ${
            isFavorite ? "ajoutée aux" : "retirée des"
          } favoris`,
        );
      } else {
        alert(t("errorManagingFavorites"));
      }
    } catch (error) {
      console.error("Erreur lors de la gestion des favoris:", error);
      alert(t("errorManagingFavorites"));
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden">
      {/* Fond cohérent avec VisitorHome */}
      <div
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat bg-slate-900"
        style={{ backgroundImage: `url(${foodBg})` }}
        aria-hidden
      />
      <div className="fixed inset-0 z-0 bg-slate-900/40" aria-hidden />

      <div className="relative z-10 w-full min-w-0 py-4 sm:py-12 md:py-16">
        <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8">
          {/* Bloc en-tête - adapté mobile */}
          <motion.div
            className={`relative backdrop-blur-lg rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-xl border p-4 sm:p-6 lg:p-8 mb-4 sm:mb-8 overflow-hidden ${
              isAdminUser
                ? "bg-slate-900/45 border-amber-300/30"
                : "bg-white dark:bg-gray-800/50 border-slate-200/50 dark:border-slate-600/50"
            }`}
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <div
              className={`absolute left-0 top-0 bottom-0 w-1 sm:w-2 rounded-r-full ${
                isAdminUser
                  ? "bg-gradient-to-b from-amber-500 to-orange-500"
                  : "bg-gradient-to-b from-orange-500 to-amber-500"
              }`}
            />
            <div className="pl-3 sm:pl-5 lg:pl-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6 mb-4 sm:mb-6">
                <div className="flex-1 min-w-0">
                  <motion.div
                    className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3"
                    variants={itemVariants}
                  >
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center text-white shadow-lg flex-shrink-0 ${
                        isAdminUser
                          ? "bg-gradient-to-br from-amber-500 to-orange-600"
                          : "bg-gradient-to-br from-orange-500 to-amber-600"
                      }`}
                    >
                      <IoRestaurant className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <div className="min-w-0">
                      <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-ink-900 dark:text-white flex flex-wrap items-center gap-2">
                        <span className="break-words">
                          {isAdminUser
                            ? tt("adminRecipesLibrary", "Bibliotheque recettes - Admin")
                            : t("allRecipes")}
                        </span>
                        {isAdminUser && (
                          <span className="text-[10px] sm:text-xs px-2 py-1 rounded-full bg-amber-500/30 border border-amber-300/40">
                            {tt("supervisionMode", "Mode supervision")}
                          </span>
                        )}
                        {isTranslating && (
                          <motion.span
                            className="text-xs sm:text-sm text-orange-300 flex items-center gap-1.5"
                            animate={{ opacity: [1, 0.5, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                          >
                            <span className="animate-spin">🌐</span>
                            {t("translating")}
                          </motion.span>
                        )}
                      </h1>
                      <p className="text-ink-600 dark:text-white/90 text-xs sm:text-sm lg:text-base mt-0.5 sm:mt-1">
                        {isAdminUser
                          ? tt(
                              "adminRecipesDescription",
                              "Consultez, filtrez et moderez les recettes de tous les utilisateurs.",
                            )
                          : t("discoverDeliciousRecipes")}
                      </p>
                    </div>
                  </motion.div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                  <motion.div
                    className={`text-center backdrop-blur-sm rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 border ${
                      isAdminUser
                        ? "bg-amber-500/15 border-amber-300/30"
                        : "bg-ink-50 border-ink-900/10 dark:bg-white/10 dark:border-white/20"
                    }`}
                    variants={itemVariants}
                  >
                    <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-ink-900 dark:text-white tabular-nums">
                      {totalCount}
                    </div>
                    <div className="text-[10px] sm:text-xs lg:text-sm text-ink-600 dark:text-white/90">
                      {t("recipes")}
                    </div>
                  </motion.div>
                  {user && (
                    <motion.button
                      onClick={() => navigate("/add-recipe")}
                      className={`inline-flex items-center justify-center gap-1.5 sm:gap-2 p-2.5 sm:px-5 sm:py-3 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all touch-manipulation ${
                        isAdminUser
                          ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                          : "bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                      }`}
                      variants={itemVariants}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <HiOutlinePlus className="w-5 h-5 shrink-0" />
                      <span className="hidden sm:inline text-sm">{t("add")}</span>
                    </motion.button>
                  )}
                </div>
              </div>

              {/* Recherche et filtres - design mobile-first */}
              <motion.div
                className="flex flex-col sm:flex-row gap-2 sm:gap-4"
                variants={itemVariants}
              >
                <div className="flex-1 relative min-w-0">
                  <HiOutlineMagnifyingGlass
                    className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none z-10"
                    aria-hidden
                  />
                  <input
                    type="text"
                    placeholder={t("searchRecipe")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-white/95 dark:bg-slate-800/90 backdrop-blur-sm border-2 border-slate-200/80 dark:border-slate-600/60 rounded-lg sm:rounded-xl focus:outline-none text-slate-800 dark:text-slate-100 placeholder-slate-400 text-sm transition-all shadow-sm ${
                      isAdminUser
                        ? "focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                        : "focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                    }`}
                  />
                </div>
                <div className="w-full sm:w-48 lg:w-52 relative flex-shrink-0">
                  <HiOutlineAdjustmentsHorizontal
                    className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none z-10"
                    aria-hidden
                  />
                  <select
                    value={filterDifficulty}
                    onChange={(e) => setFilterDifficulty(e.target.value)}
                    className={`w-full pl-10 sm:pl-12 pr-9 sm:pr-10 py-2.5 sm:py-3 bg-white/95 dark:bg-slate-800/90 backdrop-blur-sm border-2 border-slate-200/80 dark:border-slate-600/60 rounded-lg sm:rounded-xl focus:outline-none text-slate-800 dark:text-slate-100 text-xs sm:text-sm transition-all appearance-none cursor-pointer shadow-sm touch-manipulation ${
                      isAdminUser
                        ? "focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                        : "focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500"
                    }`}
                  >
                    <option value="all">{t("allDifficulties")}</option>
                    <option value="Facile">{t("easy")}</option>
                    <option value="Moyen">{t("medium")}</option>
                    <option value="Difficile">{t("hard")}</option>
                  </select>
                  <HiOutlineChevronDown
                    className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none"
                    aria-hidden
                  />
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Contenu */}
          {loading || (isTranslating && recipes.length > 0) ? (
            <motion.div
              className="bg-white dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-xl border border-ink-900/10 dark:border-white/20 p-8 sm:p-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <CardGridLoading
                message={t("loadingRecipes")}
                gridCols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                count={6}
              />
            </motion.div>
          ) : error ? (
            <motion.div
              className="bg-white dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-xl border border-ink-900/10 dark:border-white/20 p-12 sm:p-16"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">😞</div>
                <h3 className="text-xl sm:text-2xl font-semibold text-ink-900 dark:text-white mb-2">
                  {t("error")}
                </h3>
                <p className="text-ink-700 dark:text-white mb-6">{error}</p>
                <button
                  onClick={loadRecipes}
                  className={`px-6 py-3 text-white rounded-full font-semibold shadow-lg transition-all ${
                    isAdminUser
                      ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                      : "bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                  }`}
                >
                  {t("retry")}
                </button>
              </div>
            </motion.div>
          ) : translatedRecipes.length > 0 ? (
            <>
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 items-stretch"
                initial={false}
                animate={{ opacity: 1 }}
              >
                {translatedRecipes.map((recipe) => (
                  <div
                    key={recipe.id ?? recipe.title ?? String(Math.random())}
                    className="h-full"
                  >
                    <RecipeCard
                      recipe={recipe}
                      user={user}
                      showShareForAll
                      onFavoriteToggle={handleFavoriteToggle}
                      onEdit={
                        user
                          ? (recipe) => {
                              openEditPopup(recipe, () => {
                                loadRecipes();
                              });
                            }
                          : undefined
                      }
                      onDelete={
                        user
                          ? async (recipeId) => {
                              if (window.confirm(t("confirmDelete"))) {
                                try {
                                  const response = await fetch(
                                    `http://localhost:3000/api/recipes/${recipeId}`,
                                    {
                                      method: "DELETE",
                                      headers: {
                                        Authorization: `Bearer ${token}`,
                                      },
                                    },
                                  );

                                  if (response.ok) {
                                    console.log(t("recipeDeleted"));
                                    loadRecipes();
                                  } else {
                                    alert(t("errorDeleting"));
                                  }
                                } catch (error) {
                                  console.error(
                                    "Erreur lors de la suppression:",
                                    error,
                                  );
                                  alert(t("errorDeleting"));
                                }
                              }
                            }
                          : undefined
                      }
                    />
                  </div>
                ))}
              </motion.div>

              {/* Pagination - toujours visible quand il y a des recettes */}
              <motion.div
                className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-ink-100 border border-ink-900/10 text-ink-700 dark:bg-white/10 dark:hover:bg-white/20 dark:border-white/20 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                >
                  <HiChevronLeft className="w-5 h-5" />
                  <span>{t("previous") || "Précédent"}</span>
                </button>
                <span className="px-4 py-2.5 rounded-xl bg-white border border-ink-900/10 text-ink-700 dark:bg-white/10 dark:border-white/20 dark:text-white text-sm sm:text-base font-medium min-w-[7rem] text-center">
                  {t("page") || "Page"} {page} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-ink-100 border border-ink-900/10 text-ink-700 dark:bg-white/10 dark:hover:bg-white/20 dark:border-white/20 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                >
                  <span>{t("next") || "Suivant"}</span>
                  <HiChevronRight className="w-5 h-5" />
                </button>
              </motion.div>
              {totalCount > 0 && (
                <p className="mt-3 text-center text-white/70 text-sm">
                  {totalCount} {t("recipes")}{" "}
                  {totalPages > 1 ? ` · ${t("page") || "Page"} ${page}` : ""}
                </p>
              )}
            </>
          ) : (
            <motion.div
              className="bg-white dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-xl border border-ink-900/10 dark:border-white/20 p-12 sm:p-16"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">🍳</div>
                <h3 className="text-xl sm:text-2xl font-semibold text-ink-900 dark:text-white mb-2">
                  {t("noRecipeFound")}
                </h3>
                <p className="text-ink-700 dark:text-white mb-6">
                  {searchTerm || filterDifficulty !== "all"
                    ? t("noRecipeMatches")
                    : t("noRecipeAvailable")}
                </p>
                {user && (
                  <button
                    onClick={() => navigate("/add-recipe")}
                    className={`px-6 py-3 text-white rounded-full font-semibold shadow-lg transition-all ${
                      isAdminUser
                        ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                        : "bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                    }`}
                  >
                    {isAdminUser
                      ? tt("addRecipeAdmin", "Ajouter une recette (admin)")
                      : t("createFirstRecipe")}
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipesPage;
